import * as React from "react";

import { Loader } from "@progress/kendo-react-indicators";
import { getter } from "@progress/kendo-react-common";

import { Toolbar, Button, ButtonGroup } from "@progress/kendo-react-buttons";
import {
    Grid,
    GridColumn,
    GridSelectionChangeEvent,
    getSelectedState,
} from "@progress/kendo-react-grid";
import { GroupDescriptor } from '@progress/kendo-data-query';
import { ExcelExport, ExcelExportColumn, ExcelExportGroupHeaderProps } from '@progress/kendo-react-excel-export';
import { process } from "@progress/kendo-data-query";

import { useGetReportsQuery } from '../../store/sunbright-api';

export interface ExternalSummaryGridProps {
    fromDate: string;
    toDate: string;
    returnLabel: string;
    onReturn?: any;
}

const DATA_ITEM_KEY = "uid";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const initialGroup: GroupDescriptor[] = [{ field: 'region' }];

const TextOnlyGroupHeader = (props: ExcelExportGroupHeaderProps) => {
    return `${props.value}`;
};

const ExternalSummaryGrid = ({
    fromDate,
    toDate,
    returnLabel,
    onReturn,
}: ExternalSummaryGridProps) => {
    const { data: report, error: reportError, isLoading: reportLoading } = useGetReportsQuery("extsummary?from=" + fromDate + "&to=" + toDate);

    const [gridHeight, setGridHeight] = React.useState<string>("600px");
    const [excelData, setExcelData] = React.useState<any[]>([{}]);

    React.useEffect(() => {
        window.addEventListener("resize", handleResize);
    }, []);
    React.useEffect(() => {
        setGridHeight((window.innerHeight - 120) + "px");
   }, [report]);

    const handleResize = () => {
        setGridHeight((window.innerHeight - 100) + "px");
    };

    const [selectedState, setSelectedState] = React.useState<{
        [id: string]: boolean | number[];
    }>({});

    const [selectedUID, setSelectedUID] = React.useState<string>("");

    const handleSelectionChange = (e: GridSelectionChangeEvent) => {
        const newState = getSelectedState({
            event: e,
            selectedState: selectedState,
            dataItemKey: DATA_ITEM_KEY,
        })
        setSelectedState(newState);

        setSelectedUID(Object.keys(newState)[0]);
    };

    var _export = React.useRef<ExcelExport | null>(null);
    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save();
        }
    };

    return (
        <React.Fragment>
            <div>
                <Toolbar>
                    <Button
                        className="sds-buttoninbar"
                        icon="undo"
                        onClick={(e) => onReturn()}
                    >
                        {returnLabel}
                    </Button>
                    <div style={{ fontSize: "large", fontWeight: "bold", paddingLeft: "64px" }}>
                        External Services Summary {fromDate + " - " + toDate}
                    </div>
                </Toolbar>
                <div style={{ position: "fixed", top: "46px", right: "64px", zIndex: "99" }}>
                    <ButtonGroup>
                        <Button
                            themeColor={"primary"}
                            icon="file-report"
                            style={{ borderLeftColor: "white", borderLeftWidth: "1px" }}
                            onClick={excelExport}
                        >
                            Export to Excel
                        </Button>
                    </ButtonGroup>
                </div>

                {reportError ? (<>Not Authorized</>) : reportLoading ? <Loader size="large" type="converging-spinner" /> : report ?
                    (<div>
                        <ExcelExport
                            data={report}
                            fileName="ExternalSummary.xlsx"
                            collapsible={false}
                            ref={_export}
                        >
                            <ExcelExportColumn field="region" title="Region" width={110} />
                            <ExcelExportColumn title="External Performed">
                                <ExcelExportColumn field="externalExams" title="Exams" width={90} />
                                <ExcelExportColumn field="spayNeuter" title="Spay Neuter" width={90} />
                                <ExcelExportColumn field="medicalCare" title="Medical Care" width={90} />
                                <ExcelExportColumn field="microchipImplant" title="Microchip" width={90} />
                                <ExcelExportColumn field="weigh" title="Weigh" width={90} />
                                <ExcelExportColumn field="nailTrim" title="Nail Trim" width={90} />
                                <ExcelExportColumn field="grooming" title="Grooming" width={90} />
                            </ExcelExportColumn>
                            <ExcelExportColumn title="Internal Requested">
                                <ExcelExportColumn field="internalExams" title="Exams" width={90} />
                                <ExcelExportColumn field="spayNeuterRequest" title="Spay Neuter" width={90} />
                                <ExcelExportColumn field="referralRequest" title="Referral" width={90} />
                                <ExcelExportColumn field="boosterRequest" title="Booster" width={90} />
                                <ExcelExportColumn field="microchipRequest" title="Microchip" width={90} />
                            </ExcelExportColumn>
                        </ExcelExport>

                        <Grid
                            style={{ height: gridHeight }}
                            className="eventSummaryGrid"
                            data={report.map((item: any) => ({
                                ...item,
                                [SELECTED_FIELD]: selectedState[idGetter(item)]
                            }))}

                            autoProcessData={true}
                            groupable={false}

                            resizable={true}
                            size={"small"}
                            dataItemKey={DATA_ITEM_KEY}
                            selectedField={SELECTED_FIELD}
                            selectable={{
                                enabled: true,
                                mode: "single",
                            }}
                            onSelectionChange={handleSelectionChange}
                        >
                            <GridColumn field="region" title="Region" width="110px"/>
                            <GridColumn title="External Performed">
                                <GridColumn field="externalExams" title="Exams" width="90px" />
                                <GridColumn field="spayNeuter" title="Spay Neuter" width="90px" />
                                <GridColumn field="medicalCare" title="Medical Care" width="90px" />
                                <GridColumn field="microchipImplant" title="Microchip" width="90px" />
                                <GridColumn field="weigh" title="Weigh" width="90px" />
                                <GridColumn field="nailTrim" title="Nail Trim" width="90px" />
                                <GridColumn field="grooming" title="Grooming" width="90px" />
                            </GridColumn>
                            <GridColumn title="Internal Requested">
                                <GridColumn field="internalExams" title="Exams" width="90px" />
                                <GridColumn field="spayNeuterRequest" title="Spay Neuter" width="90px" />
                                <GridColumn field="referralRequest" title="Referral" width="90px" />
                                <GridColumn field="boosterRequest" title="Booster" width="90px" />
                                <GridColumn field="microchipRequest" title="Microchip" width="90px" />
                            </GridColumn>
                        </Grid>
                    </div>) : null}
           </div>
        </React.Fragment>
    );
}
export default ExternalSummaryGrid;